<template>
	<slot v-if="!loaded"></slot>

	<div v-if="loaded" class="c-step-by-step__group">
		<div class="c-step-by-step__header">
			<DsTag v-if="tag" :text="tag"></DsTag>
			<DsText
				v-if="headline"
				html-element="h2"
				class="c-step-by-step__heading"
				text-type="heading-3xl"
				text-style="regular"
			>
				<span v-html="headline"></span>
			</DsText>
		</div>
		<ul v-if="steps.length" class="c-step-by-step__list">
			<StepByStepItem
				v-for="(step, stepIndex) in steps"
				:key="step.id"
				:id="step.id"
				:index="stepIndex + 1"
				:headline="step.headline"
				:is-expanded="expandedIndex === stepIndex"
				:image-url="step.imageUrl"
				:image-alt-text="step.imageAltText"
				:content="step.content"
				:external-video-source="step.externalVideoSource"
				:play-label="step.playLabel"
				:pause-label="step.pauseLabel"
				:aspect-ratio="step.aspectRatio"
				@expand="expandedIndex = stepIndex"
			></StepByStepItem>
		</ul>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref } from 'vue';
import { useBreakpoints } from '@vueuse/core';
import { DsTag, DsText } from '@coloplast/design-system';
import StepByStepItem from './StepByStepItem.vue';

interface StepByStepItemViewModel {
	headline: string;
	content: string;
	externalVideoSource?: string;
	imageUrl?: string;
	imageAltText?: string;
	id: string;
	/* Not needed for the Vue component */
	viewPath?: string;
	index?: number;
	currentBlockId: number;
	aspectRatio?: string;
}

interface StepByStepViewModel {
	baseViewModel: StepByStepItemViewModel[];
}

const props = defineProps<{
	tag?: string;
	headline?: string;
	playLabel?: string;
	pauseLabel?: string;
	vm: StepByStepViewModel;
}>();

const breakpoints = useBreakpoints({
	sm: 640,
	md: 768,
	lg: 992,
	xl: 1200,
	'2xl': 1400,
});

const isDesktop = breakpoints.greaterOrEqual('lg');
const isTablet = breakpoints.between('md','lg');
const isMobile = breakpoints.smaller('md');

const loaded = ref(false);
const expandedIndex = ref(0);

provide('isMobile', isMobile);
provide('isTablet', isTablet);
provide('isDesktop', isDesktop);

onMounted(() => {
	loaded.value = true;
});

const steps = computed(() => {
	return props.vm.baseViewModel.map((item) => ({
		headline: item.headline,
		content: item.content,
		externalVideoSource: item.externalVideoSource,
		imageUrl: item.imageUrl,
		imageAltText: item.imageAltText,
		id: item.currentBlockId.toString(),
		playLabel: props.playLabel ?? '',
		pauseLabel: props.pauseLabel ?? '',
		aspectRatio: item.aspectRatio,
	}));
});
</script>
